import type { DialogueContent } from '../../interactions/dialogue';
import type { InspectContent } from '../../interactions/inspect';
import type { SceneDefinition } from '../scene.types';

export const friandisesInspections: Record<string, InspectContent> = {
  'friandises.drawer': {
    title: 'Tiroir secret du présentoir',
    imageUrl: '/props/prop-cageot.png',
    imageAlt: 'Tiroir entrouvert sous le présentoir',
    lines: [
      'À l’intérieur : trois miettes, une ficelle et un reçu illisible.',
      'Le reçu mentionne « peau de bœuf ×1 » à une heure où la boutique était fermée.',
      'L’enquête progresse. Lentement.',
    ],
  },
};

export const friandisesDialogues: DialogueContent[] = [
  {
    id: 'friandises.entry',
    lines: ['Le bocal n’était pas vide hier. Le principal suspect refuse de coopérer.'],
    character: 'La boutique',
    once: true,
  },
  {
    id: 'friandises.jar',
    lines: [
      'Le bocal tremble légèrement. Nous préférons ne pas l’ouvrir maintenant.',
      'Quelque chose bouge à l’intérieur. Probablement rien.',
      'Toute disparition de peau de bœuf sera considérée comme suspecte.',
    ],
  },
  {
    id: 'friandises.bag',
    lines: ['Le sachet se balance. Le bruit attire immédiatement trois chiens.'],
  },
  {
    id: 'friandises.drawer',
    lines: ['Un tiroir s’ouvre. À l’intérieur : des miettes et beaucoup de questions.'],
  },
  {
    id: 'friandises.mastication',
    lines: [
      'La mastication tombe, roule, hésite, puis remonte à sa place. Ne cherchez pas.',
      'Tout le présentoir vient de frissonner. C’est contagieux.',
    ],
  },
];

export const friandisesScene: SceneDefinition = {
  id: 'friandises',
  title: 'Le rayon Friandises et mastications',
  background: '/scenes/scene-friandises-background-v2.webp',
  entryDialogueId: 'friandises.entry',
  layers: [],
  hotspots: [
    {
      id: 'friandises.jar',
      label: 'Toucher le bocal qui tremble',
      action: 'animation',
      target: 'friandises.jar',
      x: 18,
      y: 36,
      width: 12,
      height: 9,
      hint: 'Le bocal',
    },
    {
      id: 'friandises.bag',
      label: 'Faire balancer un sachet suspendu',
      action: 'animation',
      target: 'friandises.bag',
      x: 20,
      y: 44,
      width: 10,
      height: 8,
      hint: 'Le sachet',
    },
    {
      id: 'friandises.drawer',
      label: 'Ouvrir le tiroir secret du présentoir',
      action: 'inspect',
      target: 'friandises.drawer',
      x: 15,
      y: 58,
      width: 16,
      height: 14,
      hint: 'Le tiroir',
    },
    {
      id: 'friandises.bone',
      label: 'Fouiller sous le présentoir central',
      action: 'collectible',
      target: 'bone-friandises',
      x: 36,
      y: 86,
      width: 14,
      height: 10,
      hint: 'Sous le présentoir',
    },
    {
      id: 'friandises.mastication',
      label: 'Faire tomber une grande mastication',
      action: 'animation',
      target: 'friandises.mastication',
      x: 70,
      y: 44,
      width: 10,
      height: 8,
      hint: 'La grande mastication',
      simplifiedLabel: 'Déclencher la réaction en chaîne du présentoir',
    },
    {
      id: 'friandises.product-1',
      label: 'Regarder le bois de cerf entier',
      action: 'product',
      target: 'bois-de-cerf-entier',
      x: 39,
      y: 58,
      width: 10,
      height: 10,
      hint: 'Le bois de cerf',
      simplifiedLabel: 'Voir le bois de cerf entier',
    },
    {
      id: 'friandises.product-2',
      label: 'Regarder la peau de bœuf',
      action: 'product',
      target: 'peau-de-boeuf-pour-chien-12-15-cm',
      x: 51,
      y: 58,
      width: 10,
      height: 10,
      hint: 'La peau de bœuf',
      simplifiedLabel: 'Voir la peau de bœuf',
    },
    {
      id: 'friandises.product-3',
      label: 'Regarder la corne de buffle',
      action: 'product',
      target: 'corne-de-buffle',
      x: 39,
      y: 69,
      width: 10,
      height: 10,
      hint: 'La corne',
      simplifiedLabel: 'Voir la corne de buffle',
    },
    {
      id: 'friandises.product-4',
      label: 'Regarder les oreilles de cochon',
      action: 'product',
      target: 'oreilles-de-porc',
      x: 51,
      y: 69,
      width: 10,
      height: 10,
      hint: 'Les oreilles de cochon',
      simplifiedLabel: 'Voir les oreilles de cochon',
    },
    {
      id: 'friandises.product-5',
      label: 'Regarder la trachée de bœuf',
      action: 'product',
      target: 'trachee-de-boeuf-20cm',
      x: 18,
      y: 17,
      width: 12,
      height: 10,
      hint: 'La trachée',
      simplifiedLabel: 'Voir la trachée de bœuf',
    },
    {
      id: 'friandises.product-6',
      label: 'Regarder le nerf de bœuf',
      action: 'product',
      target: 'nerf-de-boeuf',
      x: 18,
      y: 28,
      width: 12,
      height: 10,
      hint: 'Le nerf de bœuf',
      simplifiedLabel: 'Voir le nerf de bœuf',
    },
    {
      id: 'friandises.product-7',
      label: 'Regarder le bois de daim',
      action: 'product',
      target: 'bois-de-daim',
      x: 69,
      y: 17,
      width: 12,
      height: 10,
      hint: 'Le bois de daim',
      simplifiedLabel: 'Voir le bois de daim',
    },
    {
      id: 'friandises.product-8',
      label: 'Regarder les sabots de bœuf',
      action: 'product',
      target: 'sabots-de-boeuf-veau',
      x: 69,
      y: 28,
      width: 12,
      height: 10,
      hint: 'Les sabots',
      simplifiedLabel: 'Voir les sabots de bœuf',
    },
    {
      id: 'friandises.to-hall',
      label: 'Retourner au Hall',
      action: 'navigate',
      target: 'hall',
      x: 2,
      y: 40,
      width: 10,
      height: 30,
      hint: 'Retour au Hall',
      simplifiedLabel: 'Retourner au Hall',
    },
    {
      id: 'friandises.to-comptoir',
      label: 'Aller au Comptoir',
      action: 'navigate',
      target: 'comptoir',
      x: 87,
      y: 40,
      width: 11,
      height: 30,
      hint: 'Le Comptoir',
      simplifiedLabel: 'Aller au Comptoir',
    },
  ],
};
